import {Box, Typography} from "@mui/material";
import {
  Restaurant as NutritionIcon,
  FitnessCenter as WeightIcon,
} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import {getClientWeights, getClientWeightTable} from "../../../services/TraineeService";
import {listMenus} from "../../../services/NutritionV2Service";
import {isTraineeOwned, menuForDay, menuMacros} from "../../nutrition/v2/model/nutritionV2Model";
import {getTraineeProfile} from "../../../services/CoachDashboardService";
import {getErrorMessage} from "../../../enums/ErrorMessages";
import {useMessageContext} from "../../../contexts/MessageContext";
import moment from "moment";
import ClientBlocks from "../../ClientBlocks";
import ClientBodyMeasurements from "../../ClientBodyMeasurements";
import ChartCard from "../ChartCard";
import NutritionDonut from "../NutritionDonut";
import MetricsChart from "../MetricsChart";
import {t} from "../../dashboard/coach/coachTokens";
import {GoalTypes} from "../../../enums/GoalTypes";
import {useNutritionDisplayMode} from "../../../contexts/NutritionDisplayContext";
import {POINTS, energyFigure, energyLabel, macroFigure, pointsFromMacro} from "../../nutrition/v2/ui/nutritionUnits";

/* Macro colors — shared by the donut slices and the legend rows so they match */
const MACRO_COLORS = {carbs: t.green, proteins: t.blue, fats: t.amber};

/* Goal-aware calorie adherence (mirrors TraineeProfileDialog) */
const CALORIES_CUT = new Set(["AGGRESSIVE_CUT", "CUT", "MODERATE_CUT", "SHORT_CUT"]);
const CALORIES_GAIN = new Set(["MODERATE_BULK", "BULK", "AGGRESSIVE_BULK"]);
const caloriesGoalMet = (value, goal, weightGoal) => {
  if (value == null || !goal) return null;
  if (CALORIES_CUT.has(weightGoal)) return value <= goal;
  if (CALORIES_GAIN.has(weightGoal)) return value >= goal;
  return Math.abs(value - goal) / goal <= 0.1;
};

/**
 * The trainee's current overall goal: the goal of the LATEST week that has one.
 *
 * Picks by `weekDate` rather than by position in the array. The previous implementation was
 * `[...weeks].reverse().find(w => w?.goal)`, which assumed /admin/weights/table returned weeks
 * oldest-first. It returns them NEWEST-first, so reversing selected the OLDEST week — the card
 * showed "מסה" (a goal from months earlier) while the weight page correctly showed "חיטוב קצר"
 * for the same trainee. Sorting by date makes the result independent of the endpoint's ordering.
 *
 * @param {Array<{weekDate: number, goal?: string}>} weeks
 * @returns {string|null} the goal enum value, or null when no week has one.
 */
export const latestWeekGoal = (weeks) => {
  if (!Array.isArray(weeks)) return null;
  const latest = weeks
    .filter((w) => w?.goal)
    .reduce((best, w) => (best == null || w.weekDate > best.weekDate ? w : best), null);
  return latest?.goal ?? null;
};


/* ═══════════════════════════════════════════════════════════
   Main tab — coach-token redesign (v2)
   Same data/widgets as ClientPageMainSection, coach-look cards.
   ═══════════════════════════════════════════════════════════ */
const ClientMainSectionV2 = ({userId}) => {
  const navigate = useNavigate();
  const {notificationHandler} = useMessageContext();
  const mode = useNutritionDisplayMode();
  const points = mode === POINTS;
  /* The weekly figures in the header: kcal as before, or points (kcal ÷ 100) for a points company */
  const energyNum = (kcal) => (points ? energyFigure(kcal, mode).text : kcal.toLocaleString());
  /*
   * The donut's slices. In points mode they are each macro's points, so the tooltip's figure is in
   * the same unit as the centre — grams there would be the one gram figure left on the card.
   */
  const slice = (key, grams) => (points ? pointsFromMacro(key, grams) : grams);

  const [clientNutrition, setClientNutrition] = useState({loading: true, nutrition: {}});
  const [clientWeights, setClientWeights] = useState({loading: true, data: {}});
  const [adherence, setAdherence] = useState(null);
  const [goalTarget, setGoalTarget] = useState(null);

  useEffect(() => {
    // Reset first: without this the previous client's goal/adherence stay on screen during the
    // new fetch, which is the same wrong-trainee reading the deps fix addresses, just briefer.
    setClientNutrition({loading: true, nutrition: {}});
    setClientWeights({loading: true, data: {}});
    setAdherence(null);
    setGoalTarget(null);

    /*
     * V2 menus, not V1.
     *
     * This card is the first nutrition a trainer sees on a client, and it was reading the old
     * tables — so once trainers moved to the new admin it showed either nothing or a menu nobody
     * had touched in months, on the screen they land on most. The V2 menus ARE what the trainee is
     * served, so they are what this has to summarise.
     *
     * Reads only. A failure leaves the card empty rather than throwing the whole client page.
     */
    listMenus(userId).then(menus => {
      if (menus.length > 0) {
        /*
         * The menu the TRAINER prescribed, not whichever one happens to sort first.
         *
         * `menus.find(m => m.active)` took the first active row, and a trainee's own
         * "התפריט שלי" is both active and — being older than anything the trainer has written
         * since — usually first. A self-menu carries no targets, so this card showed 0 / 0 / 0
         * and "אין נתונים" for clients with a full 2,395 kcal menu sitting two rows below it,
         * which is exactly the reading a trainer would take as "I never set this client up".
         *
         * A self-menu is read-only to the trainer (the list marks it "לקריאה בלבד"), so it is
         * not what this card is summarising. It is still the fallback for a client who has only
         * ever written their own — that is a real answer, and better than an empty card.
         */
        const coachMenus = menus.filter(menu => !isTraineeOwned(menu));
        const pool = coachMenus.length > 0 ? coachMenus : menus;
        /*
         * TODAY's menu, not whichever active one comes first.
         *
         * A trainee may have several active menus at once — that is by design, the trainer offers
         * options — but they are not interchangeable: each carries the days it applies to. Taking
         * `pool.find(m => m.active)` meant a Saturday menu could be summarised on a Tuesday, and
         * the card would print its numbers as what this client is eating today. `menuForDay`
         * prefers a menu that covers today and keeps the old fallbacks behind it, so an unnamed or
         * paused menu still fills the card — which is what `isActive` below then qualifies: a
         * paused menu shown plainly reads as a client who is set up and being fed.
         *
         * It also puts the client's OWN choice first, where they have made one (`chosenByTrainee`,
         * which the server answers on this list). Two menus live for today is a normal state — the
         * trainer offers options — and until that arrived the tie was broken by sort order, which
         * could show a trainer the plan their client had deliberately moved off.
         */
        const activeMenu = menuForDay(pool);
        /*
         * What is IN the menu, not what it aims at.
         *
         * `menuMacros` reads the meals of a detailed menu and the targets of a macro/PDF one — the
         * same rule the menus list uses, so the two screens can never disagree. Reading `targets`
         * here showed a detailed menu's calculator recommendation (2,674 kcal) rather than the
         * meals the trainer actually built, which is the number the trainee eats.
         */
        const built = menuMacros(activeMenu);
        /*
         * ...falling back to the targets only when there is nothing in the menu to read. A
         * detailed menu with no meals yet sums to zero, and zeros here read as "this client has
         * no menu" for a trainer who has in fact written one and not filled it in — the targets
         * are then the only numbers it has.
         */
        const macros = built.calories > 0 ? built : (activeMenu.targets || built);
        setClientNutrition({
          loading: false,
          nutrition: {
            calories: macros.calories || 0,
            proteins: macros.protein || 0,
            carbs: macros.carbs || 0,
            fats: macros.fats || 0,
          },
          menu: {
            name: activeMenu.name,
            type: activeMenu.menuType,            // DETAILED_PLAN | MACRO_GOALS | PDF_PLAN
            isActive: !!activeMenu.active,
            /*
             * The setup strip counts COACH menus only — deliberately not `pool`, and not `menus`.
             *
             * "ערכים ✓ קיים" / "תפריט מפורט ✓ קיים" is the trainer's own checklist: it answers
             * "have I built this client's menu yet", and a ✓ is what makes them scroll past the
             * client. A trainee's self-menu is work the trainer did NOT do, so counting it lights
             * the checklist for a client nobody has set up — the same false all-clear as summarising
             * the self-menu above, and worst exactly where it lands: the client with only self-menus,
             * who has never been set up at all. Reading `coachMenus` rather than the fallback `pool`
             * means such a client shows their own targets (better than an empty card) while the strip
             * still says "חסר" — the numbers describe what the trainee eats to, the strip describes
             * what the trainer has written, and only the second one is a claim about the trainer.
             */
            hasMacro: coachMenus.some(m => m.menuType === "MACRO_GOALS"),
            hasDetailed: coachMenus.some(m => m.menuType === "DETAILED_PLAN"),
          },
        });
      } else {
        setClientNutrition({loading: false, nutrition: {}, menu: null});
      }
    }).catch(() => setClientNutrition({loading: false, nutrition: {}, menu: null}));

    // Weekly calorie adherence (avg consumed vs goal) — same source as the coach dashboard
    getTraineeProfile(userId)
        .then(r => {
          const d = r.data || {};
          setAdherence({
            avg: d.caloriesAvg,
            goal: d.caloriesGoal,
            direction: d.weightGoalDirection || d.goal,
          });
        })
        .catch(() => setAdherence(null));

    getClientWeights(userId).then(r => {
      setClientWeights({loading: false, data: r.data});
    }).catch(e => {
      notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
    });

    // Client goal — from the most recent weight-table week (same as the weight page)
    getClientWeightTable(userId, moment().subtract(8, "weeks"), moment().add(1, "week"))
        .then(r => {
          const goal = latestWeekGoal(r.data?.weeks);
          setGoalTarget(GoalTypes.find(x => x.value === goal)?.name || null);
        })
        .catch(() => setGoalTarget(null));
    // Re-fetch when the viewed client changes. This used to be `[]` with the exhaustive-deps rule
    // suppressed, so every request fired once for whichever client happened to mount first and then
    // never again — navigating to another client in the SPA left the card showing the PREVIOUS
    // trainee's menu, goal and adherence until a full page reload.
    // (Separate from the goal bug fixed in latestWeekGoal above, which was wrong even on a fresh
    // load; this one only bites when moving between clients without remounting.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
        {/* First Row: Nutrition and Weight */}
        <Box sx={{display: "flex", flexDirection: {xs: "column", md: "row"}, gap: 2, alignItems: "stretch"}}>
          {/* Nutrition Card.
              No subtitle: it used to render the active menu's free-text name (e.g. "מיניקאט"),
              which is whatever a trainer happened to type when creating the menu — and an unnamed
              menu fell back to a generic string. Not useful enough to keep. */}
          <ChartCard
              variant="coach"
              title="תזונה" icon={NutritionIcon}
              loading={clientNutrition.loading}
              /*
                A menu that is PAUSED is not what the trainee is eating, so its numbers are not
                shown as if they were. The two empty states are deliberately different sentences:
                "no menus at all" is work the trainer has not done, "none active" is work they have
                done and switched off, and only the second one is fixed from the menus list.
              */
              showData={
                !clientNutrition.loading &&
                Object.keys(clientNutrition.nutrition).length > 0 &&
                Boolean(clientNutrition.menu?.isActive)
              }
              emptyMessage={
                clientNutrition.menu
                    ? "אין תפריט פעיל — התפריט הקיים מושהה"
                    : "לא הוגדרו תפריטי תזונה"
              }
              onNavigate={() => navigate(`/app/clients/${userId}/nutrition`)}
              navigationTooltip="עבור לתזונה מלאה"
              flexBasis="50%" contentPadding="20px"
          >
            <Box sx={{display: "flex", flexDirection: "column", gap: 1.75, flex: 1}}>
              {/* Stat header: weekly avg consumption vs goal + met badge */}
              <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap"}}>
                <Box>
                  <Typography sx={{fontSize: 12, color: t.t2, fontWeight: 500, mb: 0.25}}>ממוצע צריכה שבועי</Typography>
                  <Box sx={{display: "flex", alignItems: "baseline", gap: 0.5}}>
                    <Typography sx={{fontSize: 26, fontWeight: 800, color: t.text, lineHeight: 1, letterSpacing: "-0.5px"}}>
                      {adherence?.avg != null ? energyNum(adherence.avg) : "—"}
                    </Typography>
                    {(adherence?.goal || clientNutrition.nutrition.calories) ? (
                        <Typography sx={{fontSize: 13, color: t.t3, fontWeight: 600}}>
                          / {energyNum(adherence?.goal || clientNutrition.nutrition.calories)} {energyLabel(mode)}
                        </Typography>
                    ) : <Typography sx={{fontSize: 12, color: t.t3, fontWeight: 600}}>{energyLabel(mode)}</Typography>}
                  </Box>
                </Box>
                <Box sx={{flex: 1}}/>
                {(() => {
                  const met = caloriesGoalMet(adherence?.avg, adherence?.goal, adherence?.direction);
                  if (met == null) return null;
                  return (
                      <Box sx={{
                        alignSelf: "center", fontSize: 11, fontWeight: 700,
                        padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                        background: met ? t.greenLight : t.redLight,
                        color: met ? t.greenText : t.redText,
                      }}>
                        {met ? "✓ עומד ביעד" : "✗ חורג מהיעד"}
                      </Box>
                  );
                })()}
              </Box>

              {/* Donut + macro legend */}
              <Box sx={{display: "flex", flexDirection: {xs: "column", sm: "row"}, alignItems: "center", gap: {xs: 2, sm: 2.5}}}>
                <Box sx={{flex: "0 0 auto", display: "flex", justifyContent: "center"}}>
                  <NutritionDonut
                      carbs={slice("carbs", clientNutrition.nutrition.carbs || 0)}
                      proteins={slice("protein", clientNutrition.nutrition.proteins || 0)}
                      fats={slice("fats", clientNutrition.nutrition.fats || 0)}
                      calories={clientNutrition.nutrition.calories || 0}
                      size={140}
                      sliceColors={[MACRO_COLORS.carbs, MACRO_COLORS.proteins, MACRO_COLORS.fats]}
                      emptyBorderColor={t.border}
                  />
                </Box>
                <Box sx={{flex: 1, width: "100%", display: "flex", flexDirection: "column", gap: 1}}>
                  <NutritionRow label="פחמימה" macroKey="carbs" value={clientNutrition.nutrition.carbs || 0} color={MACRO_COLORS.carbs}/>
                  <NutritionRow label="חלבון" macroKey="protein" value={clientNutrition.nutrition.proteins || 0} color={MACRO_COLORS.proteins}/>
                  <NutritionRow label="שומן" macroKey="fats" value={clientNutrition.nutrition.fats || 0} color={MACRO_COLORS.fats}/>
                </Box>
              </Box>

              {/* Menu setup strip: which menu types exist + weekly coverage */}
              {clientNutrition.menu && (
                  <Box sx={{
                    display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
                    background: t.bg, border: `1px solid ${t.border}`, borderRadius: t.rs,
                    overflow: "hidden",
                  }}>
                    <MenuPresence label="ערכים" present={clientNutrition.menu.hasMacro}/>
                    <MenuPresence label="תפריט מפורט" present={clientNutrition.menu.hasDetailed} divider/>
                    <MenuStat
                        label="מטרה"
                        value={goalTarget || "—"}
                        valueColor={goalTarget ? t.text : t.t3}
                        divider
                    />
                  </Box>
              )}
            </Box>
          </ChartCard>

          {/* Metrics Card */}
          <ChartCard
              variant="coach"
              title="מעקב נתונים" subtitle="משקל, צעדים ושינה" icon={WeightIcon}
              loading={clientWeights.loading} showData={!clientWeights.loading}
              emptyMessage="אין נתונים"
              onNavigate={() => navigate(`/app/clients/${userId}/weight`)}
              navigationTooltip="עבור למעקב מלא"
              flexBasis="50%" contentPadding="20px"
          >
            <MetricsChart data={clientWeights.data} userId={userId} chartHeight={150}/>
          </ChartCard>
        </Box>

        {/* Second Row: Training Blocks and Body Measurements */}
        <Box sx={{display: "flex", flexDirection: {xs: "column", md: "row"}, gap: 2, alignItems: "stretch"}}>
          <ClientBlocks clientId={userId} cardVariant="coach"/>
          <ClientBodyMeasurements clientId={userId} cardVariant="coach"/>
        </Box>
      </Box>
  );
};

const MenuStat = ({label, value, valueColor = t.text, divider = false}) => (
    <Box sx={{
      padding: "10px 12px", textAlign: "center",
      borderRight: divider ? `1px solid ${t.border}` : "none",
    }}>
      <Typography sx={{fontSize: 10.5, fontWeight: 600, color: t.t3, mb: 0.25, whiteSpace: "nowrap"}}>{label}</Typography>
      <Typography sx={{fontSize: 13, fontWeight: 700, color: valueColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{value}</Typography>
    </Box>
);

const MenuPresence = ({label, present, divider = false}) => (
    <Box sx={{
      padding: "10px 12px", textAlign: "center",
      borderRight: divider ? `1px solid ${t.border}` : "none",
    }}>
      <Typography sx={{fontSize: 10.5, fontWeight: 600, color: t.t3, mb: 0.4, whiteSpace: "nowrap"}}>{label}</Typography>
      <Box sx={{
        display: "inline-flex", alignItems: "center", gap: "4px",
        fontSize: 11.5, fontWeight: 700, padding: "2px 10px", borderRadius: "20px",
        background: present ? t.greenLight : t.bg,
        color: present ? t.greenText : t.t3,
        border: present ? "1px solid rgba(1,187,167,.2)" : `1px dashed ${t.border}`,
      }}>
        {present ? "✓ קיים" : "— חסר"}
      </Box>
    </Box>
);

/* A macro legend row: grams as before, or that macro's points (instead of grams) for a points company */
const NutritionRow = ({label, macroKey, value, color}) => {
  const mode = useNutritionDisplayMode();
  const figure = mode === POINTS ? macroFigure(macroKey, value, mode) : {text: value, unit: "ג'"};
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.25,
      padding: "9px 12px", borderRadius: t.rs,
      background: t.bg, border: `1px solid ${t.border}`,
    }}>
      <Box sx={{width: 10, height: 10, borderRadius: "50%", backgroundColor: color, flexShrink: 0}}/>
      <Typography sx={{fontSize: "13px", color: t.t2, fontWeight: 600}}>{label}</Typography>
      <Box sx={{flex: 1}}/>
      <Typography sx={{fontSize: "13.5px", color: t.text, fontWeight: 700}}>
        {figure.text}<Box component="span" sx={{fontSize: "11px", color: t.t3, fontWeight: 600, mr: 0.25}}> {figure.unit}</Box>
      </Typography>
    </Box>
  );
};

export default ClientMainSectionV2;
